import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { EDUCATION_LEVEL, EMPLOYMENT_STATUS } from "@/lib/enums";

const schema = z.object({
  education_level: z.string().min(1, "Education level is required"),
  field_of_study: z.string().optional(),
  occupation: z.string().trim().min(2, "Occupation is required").max(100, "Occupation must be 100 characters or less"),
  employment_status: z.string().min(1, "Employment status is required"),
  financial_status: z.number().min(1, "Financial status must be at least 1").max(5, "Financial status must be 5 or less"),
  career_description: z.string().max(1000, "Career description must be 1000 characters or less").optional(),
});

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  onBack: () => void;
  saving: boolean;
  continueLabel?: string;
  loadingLabel?: string;
}

export function Step3EducationCareer({ data, onComplete, onBack, saving, continueLabel = "Save & Continue", loadingLabel = "Saving..." }: Props) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      education_level: data.education_level,
      field_of_study: data.field_of_study,
      occupation: data.occupation,
      employment_status: data.employment_status,
      financial_status: data.financial_status || 3,
      career_description: data.career_description,
    },
  });

  // Hydrate once on mount / when re-entering this step. Do NOT reset on every
  // parent re-render — that clobbered in-flight user input (bug B2).
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    reset({
      education_level: data.education_level,
      field_of_study: data.field_of_study,
      occupation: data.occupation,
      employment_status: data.employment_status,
      financial_status: data.financial_status || 3,
      career_description: data.career_description,
    });
  }, [data, reset]);

  const financialStatus = watch("financial_status");
  const careerDescription = watch("career_description") || "";

  const getFinancialStatusLabel = (value: number) => {
    const labels = ["Fully Dependent", "Mostly Dependent", "Somewhat Independent", "Mostly Independent", "Fully Independent"];
    return labels[value - 1];
  };

  const onSubmit = (formData: any) => {
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Education</h3>
        
        <div className="space-y-2">
          <Label htmlFor="education_level">Highest education level *</Label>
          <Select value={watch("education_level")} onValueChange={(value) => setValue("education_level", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your education level" />
            </SelectTrigger>
            <SelectContent>
              {EDUCATION_LEVEL.map((edu) => (
                <SelectItem key={edu} value={edu}>{edu}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.education_level && (
            <p className="text-sm text-destructive">{errors.education_level.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="field_of_study">Field of study (optional)</Label>
          <Input
            id="field_of_study"
            {...register("field_of_study")}
            placeholder="e.g., Computer Science, Business, Medicine"
          />
          <p className="text-sm text-muted-foreground">What did you study or are you studying?</p>
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Career & Finances</h3>
        
        <div className="space-y-2">
          <Label htmlFor="occupation">Occupation/Career *</Label>
          <Input
            id="occupation"
            {...register("occupation")}
            placeholder="e.g., Software Engineer, Teacher, Student"
          />
          {errors.occupation && (
            <p className="text-sm text-destructive">{errors.occupation.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="employment_status">Employment status *</Label>
          <Select value={watch("employment_status")} onValueChange={(value) => setValue("employment_status", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your employment status" />
            </SelectTrigger>
            <SelectContent>
              {EMPLOYMENT_STATUS.map((status) => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.employment_status && (
            <p className="text-sm text-destructive">{errors.employment_status.message}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Financial independence *</Label>
            <span className="text-sm font-medium text-primary">
              {financialStatus} - {getFinancialStatusLabel(financialStatus)}
            </span>
          </div>
          <Slider
            value={[financialStatus]}
            onValueChange={(value) => setValue("financial_status", value[0])}
            min={1}
            max={5}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 - Dependent</span>
            <span>3 - Somewhat</span>
            <span>5 - Independent</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="career_description">Career description (optional)</Label>
            <span className="text-sm text-muted-foreground">{careerDescription.length}/1000</span>
          </div>
          <Textarea
            id="career_description"
            {...register("career_description")}
            placeholder="Tell us about your career goals, current role, or professional interests..."
            rows={3}
            maxLength={1000}
          />
          <p className="text-sm text-muted-foreground">
            Share your career aspirations and professional journey
          </p>
        </div>
      </div>

      <div className="flex justify-between gap-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            continueLabel
          )}
        </Button>
      </div>
    </form>
  );
}
